import React, { FC, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import isNumber from 'lodash/isNumber';
import { useDispatch, useSelector } from 'react-redux';

import { MenuItemEnum, SidebarModelResponse } from 'src/models/Partner/Sidebar';
import { useTHelper } from 'src/utils/i18n';
import { ROUTE_ORGANIZER_CAMPAIGNS } from 'src/routes';
import { CAMPAIGNS_SHOW_CREATE_MODAL_PARAM } from 'src/config';
import { useStateForm } from 'src/utils/stateForm';
import { UiCleanDropdown } from 'src/components/UI/dropdown/CleanDropdown';
import { useOneRequest } from 'src/utils/hooks/useOneRequest';
import { apiClearRecentlyEndedCampaigns, apiSetCurrentCampaign } from 'src/api/account/profile';
import { UiSpinner } from 'src/components/UI/Spinner';
import { UiCleanDropdownProps } from 'src/components/UI/dropdown/types';
import { commonSlice } from 'src/store/commonPart/slice';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { getUniqueId } from 'src/utils/getUniqueId';
import { UiButton } from 'src/components/UI/Button';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';
import { store } from 'src/store/store';
import styles from './styles.module.scss';
import sidebarStyles from '../../styles.module.scss';
import { useStaticCampaignOptionsMenuItems } from './hooks/useStaticCampaignOptionsMenuItems';
import { ItemLink } from '../common/Item';

type Props = {
  hasOnlyDraftCampaigns: SidebarModelResponse['HasOnlyDraftCampaigns'];
  selectedCampaignMenuItems: SidebarModelResponse['SelectedCampaignMenuItems'];
  organizerCampaigns: SidebarModelResponse['OrganizerCampaigns'];
  selectedItemId: MenuItemEnum | null;
  selectedCampaignId: number;
  disabledItems: boolean;
  canCreateCampaign: boolean;
  isCurrentPageSPA: boolean;
};

type FormValues = {
  campaignId: number;
};

export const CampaignOptions: FC<Props> = ({
  hasOnlyDraftCampaigns,
  selectedCampaignMenuItems,
  organizerCampaigns,
  selectedItemId,
  selectedCampaignId,
  disabledItems,
  canCreateCampaign,
  isCurrentPageSPA,
}) => {
  const tHelper = useTHelper('leftMenu.campaignOptions');

  const dispatch = useDispatch();

  const formProps = useStateForm<FormValues>({
    defaultValues: {
      campaignId: selectedCampaignId,
    },
  });

  const { Name: partnerName } = useSelector(deCommonCurrentPartnerSelector);

  const { apiCaller, loading } = useOneRequest(apiSetCurrentCampaign);

  const { main, quickLinks, otherQuickLinks } = useStaticCampaignOptionsMenuItems(selectedCampaignMenuItems);

  const campaignsItems = useMemo(
    () => organizerCampaigns.map(({ ID, Name }) => ({ value: ID, label: `${ID} - ${Name}` })),
    [organizerCampaigns]
  );

  const { apiCaller: apiCallerClearRecently } = useOneRequest(apiClearRecentlyEndedCampaigns);

  const clearRecentlyCampaigns = useCallback(() => {
    apiCallerClearRecently().then(() => {
      const globalNavigationResponse = store.getState().common.globalNavigation.response;

      if (globalNavigationResponse) {
        const activeCampaigns = globalNavigationResponse.OrganizerCampaigns.filter(({ IsEnded }) => !IsEnded);

        /** the case when the "recently" campaign is selected */
        if (
          activeCampaigns.length &&
          !activeCampaigns.some(({ ID }) => globalNavigationResponse.SelectedCampaignID === ID)
        ) {
          apiCaller({ campaignId: activeCampaigns[0].ID }).then((response) => {
            dispatch(commonSlice.actions.changeGlobalNavigationCampaign(response));
          });
        } else {
          dispatch(
            commonSlice.actions.changeGlobalNavigationCampaign({
              OrganizerCampaigns: activeCampaigns,
            })
          );
        }
      }
    });
  }, [apiCaller, apiCallerClearRecently, dispatch]);

  const campaignsCategories = useMemo<UiCleanDropdownProps['categories']>(() => {
    const recentlyCategory = {
      id: getUniqueId(),
      component: (
        <div className={styles.category}>
          <span>{tHelper('categories.recently')}</span>
          <span>-</span>
          <UiButton notBtn className={styles.dismissBtn} onClick={clearRecentlyCampaigns}>
            {tHelper('categories.dismiss')}
          </UiButton>
        </div>
      ),
      values: organizerCampaigns.filter(({ IsEnded }) => IsEnded).map(({ ID }) => ID),
    };

    if (!recentlyCategory.values.length) {
      return undefined;
    }

    return [
      recentlyCategory,
      {
        id: getUniqueId(),
        component: (
          <div className={styles.category}>
            <span>{tHelper('categories.active')}</span>
          </div>
        ),
        values: organizerCampaigns.filter(({ IsEnded }) => !IsEnded).map(({ ID }) => ID),
      },
    ];
  }, [clearRecentlyCampaigns, organizerCampaigns, tHelper]);

  const getQueryParams = (id: MenuItemEnum) => (selectedItemId === id ? `${window.location.search}&` : '?');

  const handleChangeCampaign = useCallback<NonNullable<UiCleanDropdownProps['onChange']>>(
    (value) => {
      if (selectedCampaignId !== value && isNumber(value)) {
        apiCaller({ campaignId: value }).then((response) => {
          dispatch(commonSlice.actions.changeGlobalNavigationCampaign(response));
        });
      }
    },
    [apiCaller, dispatch, selectedCampaignId]
  );

  if (organizerCampaigns.length === 0) {
    const isCreateCampaignDisabled = !hasOnlyDraftCampaigns && !canCreateCampaign;
    const linkClassName = `btn ${styles.linkBtn}`;
    const linkText = hasOnlyDraftCampaigns ? tHelper('viewCampaigns') : tHelper('createCampaign');
    const linkTo = hasOnlyDraftCampaigns
      ? ROUTE_ORGANIZER_CAMPAIGNS
      : `${ROUTE_ORGANIZER_CAMPAIGNS}${getQueryParams(
          MenuItemEnum.MyCampaigns
        )}${CAMPAIGNS_SHOW_CREATE_MODAL_PARAM}=true`;

    const handleDisabledClick = (e: React.MouseEvent) => {
      if (isCreateCampaignDisabled) {
        e.preventDefault();
      }
    };

    return (
      <>
        <div className={styles.infoContainer}>
          <div className={styles.infoCard}>{tHelper('noCampaignsInfo')}</div>
        </div>
        <UiHoverTooltip text={tHelper('unavailableArea', { partnerName })} render={isCreateCampaignDisabled}>
          <div className={styles.actionsContainer}>
            {isCurrentPageSPA ? (
              <Link
                replace
                to={linkTo}
                className={linkClassName}
                onClick={handleDisabledClick}
                style={isCreateCampaignDisabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
              >
                {linkText}
              </Link>
            ) : (
              <UiInnerNotSPALink href={linkTo} className={linkClassName} disabled={isCreateCampaignDisabled}>
                {linkText}
              </UiInnerNotSPALink>
            )}
          </div>
        </UiHoverTooltip>
      </>
    );
  }

  const disabledLinks = disabledItems || loading;

  return (
    <>
      <div className={styles.actionsContainer}>
        <UiCleanDropdown
          material
          formProps={formProps}
          name="campaignId"
          items={campaignsItems}
          className={`${styles.campaignsDropdown} ${sidebarStyles.campaignsDropdown}`}
          disabled={loading}
          onChange={handleChangeCampaign}
          showTitle
          categories={campaignsCategories}
        />
      </div>

      <div className={styles.linksWrapper}>
        {loading && <UiSpinner small className={styles.spinner} />}

        {main.map(({ id, title, url, readyAsSPA, openNewTab, testId }) => (
          <ItemLink
            key={id}
            title={title}
            url={url}
            readyAsSPA={readyAsSPA}
            openNewTab={openNewTab}
            testId={testId}
            subItem
            disabled={disabledLinks}
            isCurrentPageSPA={isCurrentPageSPA}
            selected={id === selectedItemId}
          />
        ))}

        {!!quickLinks.length && (
          <>
            <div className={styles.subTitle}>{tHelper('subTitle.eventQuickLinks')}</div>

            {quickLinks.map(({ id, title, url, readyAsSPA, openNewTab, testId }) => (
              <ItemLink
                key={id}
                title={title}
                url={url}
                readyAsSPA={readyAsSPA}
                openNewTab={openNewTab}
                testId={testId}
                subItem
                disabled={disabledLinks}
                isCurrentPageSPA={isCurrentPageSPA}
                selected={id === selectedItemId}
              />
            ))}
          </>
        )}

        {!!otherQuickLinks.length && (
          <>
            {quickLinks.length ? (
              <div className={styles.subTitle}>{tHelper('subTitle.otherQuickLinks')}</div>
            ) : (
              <div className={styles.subTitle}>{tHelper('subTitle.quickLinks')}</div>
            )}

            {otherQuickLinks.map(({ id, title, url, readyAsSPA, openNewTab, testId }) => (
              <ItemLink
                key={id}
                title={title}
                url={url}
                readyAsSPA={readyAsSPA}
                openNewTab={openNewTab}
                testId={testId}
                subItem
                disabled={disabledLinks}
                isCurrentPageSPA={isCurrentPageSPA}
                selected={id === selectedItemId}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
};
